import type { NavigationGuard } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { useWorkspaceStore } from "@/stores/workspace";

export const authGuard: NavigationGuard = (to, _from, next) => {
  const auth = useAuthStore();
  if (!auth.isSignedIn) {
    next({ name: "signin", query: { redirect: to.fullPath } });
    return;
  }
  if (!auth.isAllowedDomain) {
    next({ name: "wrong-domain" });
    return;
  }
  next();
};

export function roleGuard(...allowed: Array<"admin" | "viewer" | "user">): NavigationGuard {
  return (to, _from, next) => {
    const auth = useAuthStore();
    if (!auth.isSignedIn) {
      next({ name: "signin", query: { redirect: to.fullPath } });
      return;
    }
    if (!auth.isAllowedDomain) {
      next({ name: "wrong-domain" });
      return;
    }
    if (!allowed.includes(auth.role)) {
      next({ name: "forbidden" });
      return;
    }
    next();
  };
}

/** The welcome comes first (onboarding, 2026-10-26): someone in no workspace who never asked to join one sees only it,
 * until they're in one or have sent a request. The server decides (/me's onboarding.gated) and refuses the console's
 * other routes itself; this spares them a page that would only be refused. A page they asked for waits in `next`. */
export const onboardingGuard: NavigationGuard = async (to) => {
  if (to.meta.public || to.meta.bare || to.meta.bareWhenHeld || !useAuthStore().isSignedIn) return true;   // feedback: own reports
  const workspace = useWorkspaceStore();
  await workspace.load();
  // open only when /me said so: a failed /me isn't "open" (the welcome says it couldn't check, and retries)
  if (workspace.state === "ready" && workspace.me && !workspace.me.onboarding?.gated) return true;
  return { name: "welcome", query: to.fullPath === "/" ? {} : { next: to.fullPath } };
};
